import {
  batch,
  effect,
  signal,
  Signal,
  useComputed,
} from '@preact/signals-react';
import { useContext, useEffect } from 'react';
import produce from 'immer';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { EditorContext } from '@/features/editor/context/editor-context';
import { CompoundChange } from '@/pkg/sdk';
import {
  PLACEHOLDER_CSS,
  PENDING_PLACEHOLDER_HTML,
  PLACEHOLDER_JS,
} from '@/features/editor/widgets/shared/ai-editing-layout/placeholders';
import { debounced } from '@/signals/utils';
import {
  EditorConfig,
  sanitizeKeyName,
} from '@/features/ai-agents/chat/editor-config';

export function useCompoundChangeRenderer(
  change: Signal<EditorDeclarativeBlock>,
  isProcessing: Signal<boolean>,
) {
  const {
    applyTempChange,
    previewLoading,
    devicePreview: {
      updateCompoundWidgetProps,
      iframeRef,
      editorState: { device },
    },
  } = useContext(EditorContext);

  const agentId = (change.value.block.value as CompoundChange).selectedAgent.id;

  const isReRenderRequired = signal(true);

  const isLegacyPlaceholder = useComputed(() =>
    (change?.value?.block?.value as CompoundChange)?.html?.includes(
      `__loomi_element_placeholder`,
    ),
  );

  const notifyState = () => {
    setTimeout(() => {
      iframeRef?.current?.contentWindow?.postMessage(
        {
          tpe: `vsly-placeholder-state-changed`,
          state: isProcessing.value ? `loading` : `idle`,
        },
        `*`,
      );
    }, 500);
  };

  effect(() => {
    isProcessing.value;
    setTimeout(notifyState, 100);
  });

  const { value: debouncedChange } = debounced(change, 100);

  const lastSyncedCode = signal<{ html: string; css: string; js: string }>({
    html: ``,
    css: ``,
    js: ``,
  });

  const wrapSectionModifierHtml = (newHtml: string) => {
    // Section modifiers should not render any HTML in the preview, only templates
    // but for optimization (getting the lmid for re-renders optimization) we need a wrapper component
    // This will ensure that on the editor we will have this
    if (agentId === `section-modifier`) {
      newHtml = `<div style="display: none">
{newHtml}
</div>`;
    }
    return newHtml;
  };
  const render = (
    newChange: EditorDeclarativeBlock,
    shouldRerender: boolean,
  ) => {
    if (newChange) {
      if (isLegacyPlaceholder.value) {
        const loadingChange = produce(newChange, (draft) => {
          const val = draft.block.value as CompoundChange;
          val.html = PENDING_PLACEHOLDER_HTML;
          val.css = PLACEHOLDER_CSS;
          val.js = PLACEHOLDER_JS;
        });
        applyTempChange(loadingChange);
        isReRenderRequired.value = false;
        return;
      }

      const value = newChange.block.value as CompoundChange;
      let newHtml = value.html;
      const newCss = value.css;
      const newJs = combineWithEditorConfig(value.js, value.editorConfig);

      newHtml = wrapSectionModifierHtml(newHtml);

      const isHtmlChanged = newHtml !== lastSyncedCode.peek().html;
      const isCssChanged = newCss !== lastSyncedCode.peek().css;
      const isJsChanged = newJs !== lastSyncedCode.peek().js;

      console.log(`mmm`, {
        isHtmlChanged,
        isCssChanged,
        isJsChanged,
        shouldRerender,
        lastSyncedCode: lastSyncedCode.peek(),
        newCode: { html: newHtml, css: newCss, js: newJs },
      });

      if (!isHtmlChanged && !isCssChanged && !isJsChanged && !shouldRerender) {
        console.debug(`CompoundChangeRenderer`, `No changes detected`, {
          lastSyncedCode,
          value,
        });
        return;
      }

      if (shouldRerender) {
        console.debug(
          `CompoundChangeRenderer`,
          `Applying change (full re-render)`,
          newChange,
        );
        const combinedJs = produce(newChange, (draft) => {
          const value = draft.block.value as CompoundChange;
          value.js = newJs;
        });
        applyTempChange(combinedJs);
        isReRenderRequired.value = false;
      } else {
        console.debug(
          `CompoundChangeRenderer`,
          `Applying change (quick swap)`,
          newChange,
        );
        const div = document.createElement(`div`);
        div.innerHTML = value.html;
        updateCompoundWidgetProps(
          `#${newChange.editorId}`,
          isHtmlChanged || isJsChanged ? newHtml : `noop`,
          isCssChanged ? newCss : `noop`,
          isHtmlChanged || isJsChanged ? newJs : `noop`,
        );
      }

      lastSyncedCode.value = {
        html: newHtml,
        css: newCss,
        js: newJs,
      };
    }
  };
  effect(() => {
    const newChange = debouncedChange.value;
    const shouldRerender = isReRenderRequired.value;
    render(newChange, shouldRerender);
  });

  useEffect(() => {
    if (!previewLoading) {
      setTimeout(() => {
        invalidate();
      }, 1000);
    }
  }, [previewLoading, device]);

  const rerender = () => {
    applyTempChange(
      produce(debouncedChange.value, (draft) => {
        const block = draft.block.value as CompoundChange;
        block.html = ``;
      }),
    );
    setTimeout(() => render(debouncedChange.value, true), 500);
  };

  const invalidate = () => {
    batch(() => {
      lastSyncedCode.value = { html: ``, css: ``, js: `` };
      isReRenderRequired.value = true;
    });
  };

  return {
    rerender,
    invalidate,
  };
}

function combineWithEditorConfig(
  js: string,
  editorConfig?: EditorConfig,
): string {
  let configCode = `\n/* Editor Config */\n`;
  if (editorConfig) {
    Object.keys(editorConfig?.actions || {}).forEach((key) => {
      const action = editorConfig.actions[key];
      const sanitizedKey = sanitizeKeyName(key);
      configCode += `
function __vsly_action_${sanitizedKey}() {
  /* ${action.description} */
  ${action.code}
}
document.currentWidget_action_${sanitizedKey} = __vsly_action_${sanitizedKey};
`;
    });

    Object.keys(editorConfig?.states || {}).forEach((key) => {
      const state = editorConfig.states[key];
      const sanitizedKey = sanitizeKeyName(key);
      configCode += `
function __vsly_state_${sanitizedKey}() {
  /* ${state.description} */
  ${state.code}
}
document.currentWidget_state_${sanitizedKey} = __vsly_state_${sanitizedKey}`;
    });

    if (editorConfig?.cleanup?.code) {
      configCode += `
function __vsly_cleanup() {
  /* Cleanup function */
  ${editorConfig.cleanup.code}
}
document.currentWidget_cleanup = __vsly_cleanup`;
    }
  }

  return `${js}\n${configCode}`;
}
